import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  Platform,
  ScrollView,
} from 'react-native';
import { ScreenLayout, Header, FormHeader } from '../components';
import { ChannelList, ChannelBindingModal } from '../components/channels';
import { Button } from '../components/design-system';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface ManageCatalogueChannelsScreenProps {
  catalogueId: string;
}

export const ManageCatalogueChannelsScreen: React.FC<ManageCatalogueChannelsScreenProps> = ({
  catalogueId,
}) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, activeTab, setActiveTab } = useAppContext();
  const [catalogue, setCatalogue] = useState<any>(null);
  const [availableChannels, setAvailableChannels] = useState<any[]>([]);
  const [bindings, setBindings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showBindingModal, setShowBindingModal] = useState(false);

  useEffect(() => {
    loadData();
  }, [catalogueId]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadCatalogue(),
        loadAvailableChannels(),
        loadBindings(),
      ]);
    } catch (error: any) {
      console.error('Error loading data:', error);
      showNotification('error', 'Error', 'Failed to load data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadCatalogue = async () => {
    try {
      const response = await apiService.getCatalogueById(catalogueId);
      if (response.statusCode === 1000 && response.data) {
        setCatalogue(response.data);
      }
    } catch (error: any) {
      console.error('Error loading catalogue:', error);
    }
  };

  const loadAvailableChannels = async () => {
    try {
      const response = await apiService.getAvailableChannels();
      if (response.statusCode === 1000 && response.data) {
        setAvailableChannels(response.data || []);
      }
    } catch (error: any) {
      console.error('Error loading available channels:', error);
    }
  };

  const loadBindings = async () => {
    try {
      const response = await apiService.getCatalogueChannelBindings(catalogueId);
      if (response.statusCode === 1000 && response.data) {
        setBindings(response.data || []);
      }
    } catch (error: any) {
      console.error('Error loading bindings:', error);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  const handleBind = async (channel: string) => {
    try {
      const response = await apiService.bindCatalogueToChannel(catalogueId, channel);
      if (response.statusCode === 1000) {
        showNotification('success', 'Success', `Catalogue bound to ${channel} successfully`);
        loadBindings();
        loadAvailableChannels();
      } else {
        const errorMessage = response.errors?.[0]?.message || 'Failed to bind catalogue to channel';
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Failed to bind catalogue to channel');
    }
  };

  const handleUnbind = async (channel: string) => {
    try {
      const response = await apiService.unbindCatalogueFromChannel(catalogueId, channel);
      if (response.statusCode === 1000) {
        showNotification('success', 'Success', `Catalogue unbound from ${channel} successfully`);
        loadBindings();
        loadAvailableChannels();
      } else {
        const errorMessage = response.errors?.[0]?.message || 'Failed to unbind catalogue from channel';
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Failed to unbind catalogue from channel');
    }
  };

  const handleBulkBind = async (channels: string[]) => {
    try {
      // Get currently bound channels
      const currentChannels = bindings.map(b => b.channel);
      
      // Channels to bind (new ones)
      const toBind = channels.filter(ch => !currentChannels.includes(ch));
      
      // Channels to unbind (removed ones)
      const toUnbind = currentChannels.filter(ch => !channels.includes(ch));

      // Bind new channels
      for (const channel of toBind) {
        await apiService.bindCatalogueToChannel(catalogueId, channel);
      }

      // Unbind removed channels
      for (const channel of toUnbind) {
        await apiService.unbindCatalogueFromChannel(catalogueId, channel);
      }

      showNotification('success', 'Success', 'Channels updated successfully');
      loadBindings();
      loadAvailableChannels();
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Failed to update channel bindings');
      throw error;
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  if (loading && !catalogue) {
    return (
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
        onLeftPaneClose={() => {}}
        leftPaneContent="settings"
        onSettingsItemPress={async (itemId) => {
          const { setPendingSettingsItem } = await import('../utils/tempStorage');
          await setPendingSettingsItem(itemId);
          navigateTo('settings');
        }}
        onRightPaneClose={() => {}}
        formHeader={
          <FormHeader
            title="Manage Channels"
            onBack={() => {
              if (Platform.OS === 'web') {
                window.history.back();
              } else {
                navigateTo('catalogue-detail', catalogueId);
              }
            }}
          />
        }
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Loading...</Text>
        </View>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={
        <FormHeader
          title={catalogue?.name ? `Channels - ${catalogue.name}` : 'Manage Channels'}
          onBack={() => {
            if (Platform.OS === 'web') {
              window.history.back();
            } else {
              navigateTo('catalogue-detail', catalogueId);
            }
          }}
        />
      }
    >
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.description}>
            Bind this catalogue to one or more channels to enable product syncing and catalog messaging.
          </Text>
          <Button
            variant="primary"
            onPress={() => setShowBindingModal(true)}
            style={styles.bindButton}
          >
            <Text style={styles.bindButtonText}>Manage Channels</Text>
          </Button>
        </View>

        <ChannelList
          channels={availableChannels}
          bindings={bindings}
          loading={loading}
          refreshing={refreshing}
          onRefresh={handleRefresh}
          onBind={handleBind}
          onUnbind={handleUnbind}
          emptyMessage="No channels available. Please connect channels first."
        />

        <ChannelBindingModal
          visible={showBindingModal}
          onClose={() => setShowBindingModal(false)}
          onConfirm={handleBulkBind}
          availableChannels={availableChannels}
          currentBindings={bindings}
          catalogueName={catalogue?.name}
          loading={loading}
        />
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  header: {
    padding: spacing.md,
    backgroundColor: colors.background.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.border + '40',
  },
  description: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    marginBottom: spacing.md,
    lineHeight: 20,
  },
  bindButton: {
    alignSelf: 'flex-start',
  },
  bindButtonText: {
    color: colors.white,
    fontFamily: typography.fontFamily.semibold,
    fontWeight: '600',
  },
});

